export class InputController {
  private readonly keys = new Set<string>();
  private pointerX = 0.5;
  private pointerActive = false;
  private launchQueued = false;
  private pauseQueued = false;
  private restartQueued = false;
  private lockAllowed = false;
  private pointerLocked = false;

  private readonly onKeyDown = (event: KeyboardEvent) => {
    this.keys.add(event.code);
    if (event.code === 'Space' || event.code === 'Escape') {
      event.preventDefault();
      this.launchQueued = true;
      // Keyboard-only pause: clicks launch but never pause. Space does both.
      this.pauseQueued = true;
    }
    if (event.code === 'KeyR') {
      this.restartQueued = true;
    }
  };

  private readonly onKeyUp = (event: KeyboardEvent) => {
    this.keys.delete(event.code);
  };

  private readonly onPointerMove = (event: PointerEvent) => {
    const rect = this.canvas.getBoundingClientRect();
    if (rect.width <= 0) return;
    if (this.pointerLocked) {
      // Relative motion while the OS cursor is captured. Scale movementX by
      // canvas width so paddle sensitivity matches the unlocked mapping.
      this.pointerX = Math.min(1, Math.max(0, this.pointerX + event.movementX / rect.width));
      this.pointerActive = true;
      return;
    }
    this.pointerX = (event.clientX - rect.left) / rect.width;
    this.pointerActive = true;
  };

  private readonly onPointerDown = (event: PointerEvent) => {
    if (event.target instanceof HTMLElement && event.target.closest('#sample-panel, button, label, input')) {
      return;
    }
    const rect = this.canvas.getBoundingClientRect();
    if (rect.width > 0 && !this.pointerLocked) {
      this.pointerX = (event.clientX - rect.left) / rect.width;
      this.pointerActive = true;
    }
    this.launchQueued = true;
    // Grab pointer lock on mouse clicks during play. Skip for touch/pen — the
    // Pointer Lock API is a no-op on touchscreens and would just fail silently.
    if (this.lockAllowed && event.pointerType === 'mouse' && !this.pointerLocked) {
      this.requestPointerLock();
    }
  };

  private readonly onLaunchDown = (event: PointerEvent) => {
    event.preventDefault();
    this.launchQueued = true;
  };

  private readonly onPointerLockChange = () => {
    const nowLocked = document.pointerLockElement === this.canvas;
    if (this.pointerLocked && !nowLocked && this.lockAllowed) {
      // Lock released mid-play (Esc, alt-tab, browser). Queue a pause so the
      // game freezes even in browsers that swallow the Esc keydown.
      this.pauseQueued = true;
      this.launchQueued = true;
    }
    this.pointerLocked = nowLocked;
  };

  constructor(
    private readonly canvas: HTMLCanvasElement,
    private readonly launchButton: HTMLElement | null,
    /** Optional wider surface (e.g. the game stack including the mobile
     *  thumb-rest strip below the canvas) that should also trigger paddle
     *  drag when touched. Touchscreens don't fire pointermove without a
     *  preceding pointerdown, so a touch that starts in the thumb zone
     *  needs a pointerdown target outside the canvas to be trackable. */
    private readonly touchSurface: HTMLElement | null = null,
  ) {
    window.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('keyup', this.onKeyUp);
    window.addEventListener('pointermove', this.onPointerMove);
    this.canvas.addEventListener('pointerdown', this.onPointerDown);
    this.touchSurface?.addEventListener('pointerdown', this.onPointerDown);
    this.launchButton?.addEventListener('pointerdown', this.onLaunchDown);
    document.addEventListener('pointerlockchange', this.onPointerLockChange);
  }

  /** Returns paddle target as normalized 0..1 across the field. */
  getPaddleNormX(): number {
    let keyboard = 0;
    if (this.keys.has('KeyA') || this.keys.has('ArrowLeft')) keyboard -= 1;
    if (this.keys.has('KeyD') || this.keys.has('ArrowRight')) keyboard += 1;

    if (keyboard !== 0 && !this.pointerActive) {
      // Keyboard users: accumulate from last pointer position.
      this.pointerX = Math.min(1, Math.max(0, this.pointerX + keyboard * 0.02));
    }
    return Math.min(1, Math.max(0, this.pointerX));
  }

  /** Continuous keyboard axis for smooth paddle motion. */
  getKeyboardAxis(): number {
    let axis = 0;
    if (this.keys.has('KeyA') || this.keys.has('ArrowLeft')) axis -= 1;
    if (this.keys.has('KeyD') || this.keys.has('ArrowRight')) axis += 1;
    return axis;
  }

  isPointerDriven(): boolean {
    return this.pointerActive;
  }

  consumeLaunch(): boolean {
    const value = this.launchQueued;
    this.launchQueued = false;
    return value;
  }

  consumePause(): boolean {
    const value = this.pauseQueued;
    this.pauseQueued = false;
    return value;
  }

  /** External trigger (e.g. mobile pause button) that mirrors the keyboard's
   *  Escape/Space pause behavior — queues both a launch and a pause so the
   *  game loop treats it like a Space press. */
  queuePauseToggle(): void {
    this.launchQueued = true;
    this.pauseQueued = true;
  }

  consumeRestart(): boolean {
    const value = this.restartQueued;
    this.restartQueued = false;
    return value;
  }

  /** Toggle whether canvas clicks acquire pointer lock. Turning this off also
   *  releases any active lock so the OS cursor is visible for overlay clicks. */
  setPointerLockAllowed(allowed: boolean): void {
    this.lockAllowed = allowed;
    if (!allowed && this.pointerLocked) {
      document.exitPointerLock();
    }
  }

  isPointerLocked(): boolean {
    return this.pointerLocked;
  }

  /** Call from a user gesture (button click) to acquire pointer lock without
   *  a direct canvas click. Browsers require an active gesture for the
   *  request to succeed — call synchronously from the handler. */
  requestPointerLock(): void {
    if (this.pointerLocked) return;
    const request = this.canvas.requestPointerLock?.bind(this.canvas);
    if (!request) return;
    // Newer browsers return a Promise; older ones return void. Swallow either.
    const result = request() as Promise<void> | undefined;
    result?.catch(() => {});
  }

  dispose(): void {
    window.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('keyup', this.onKeyUp);
    window.removeEventListener('pointermove', this.onPointerMove);
    this.canvas.removeEventListener('pointerdown', this.onPointerDown);
    this.touchSurface?.removeEventListener('pointerdown', this.onPointerDown);
    this.launchButton?.removeEventListener('pointerdown', this.onLaunchDown);
    document.removeEventListener('pointerlockchange', this.onPointerLockChange);
    if (this.pointerLocked) document.exitPointerLock();
  }
}
